import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Flex, HStack, Icon, Input, Popover, PopoverBody,
  PopoverContent, PopoverTrigger, Text, VStack, useColorModeValue, useDisclosure,
} from "@chakra-ui/react";
import { ChevronDown, User, UserPlus, X } from "lucide-react";
import { UserAvatar } from "../../../../components/UserAvatar";
import { getUser } from "../../../../lib/auth";
import { listCollaborators, type Collaborator } from "../../../../lib/api";
import { useHrSectionStyles } from "./useHrSectionStyles";

export interface RecruiterSectionProps {
  recruiterEmail: string | null;
  onAssign: (email: string | null, name: string | null) => void;
  isSaving: boolean;
  token: string;
  /** "card" = bloco destacado (etapa). "header" = linha integrada ao cabeçalho
   * do drawer, sem bordas/alerta. */
  variant?: "card" | "header";
}

/**
 * Recrutador responsável: permite atribuir uma recrutadora (usuário do GG Hub)
 * ao candidato. O próprio bloco com o nome/avatar é o gatilho do dropdown
 * (estilo Jira) — sem botão separado. Quando não há responsável, mostra um
 * atalho "Atribuir a mim" abaixo, também no estilo Jira.
 */
export const RecruiterSection = ({
  recruiterEmail, onAssign, isSaving, token, variant = "card",
}: RecruiterSectionProps) => {
  const section = useHrSectionStyles(!!recruiterEmail);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const inputBg        = useColorModeValue("white", "gray.700");
  const muted          = useColorModeValue("gray.400", "gray.500");
  const dropdownBg     = useColorModeValue("white", "#1a2f4a");
  const itemHover      = useColorModeValue("rgba(0,158,251,0.07)", "rgba(0,158,251,0.12)");
  const rowBorder      = useColorModeValue("gray.200", "whiteAlpha.200");
  const rowHoverBorder = useColorModeValue("blue.300", "blue.400");
  const rowHoverBg     = useColorModeValue("gray.50", "whiteAlpha.100");
  const emptyAvatarBg  = useColorModeValue("gray.200", "whiteAlpha.200");
  const nameClr        = useColorModeValue("gray.800", "white");
  const linkClr        = useColorModeValue("blue.600", "blue.300");
  // Ícone de pessoa e label externo (fora do campo) — preto no claro, branco
  // no escuro, pra ficarem bem visíveis (mais contraste que o `muted`).
  const strongClr      = useColorModeValue("black", "white");

  const me = getUser();
  const meEmail = me?.email ?? null;

  const fetchCollaborators = useCallback((q: string) => {
    setLoading(true);
    listCollaborators(token, q || undefined)
      .then(setCollaborators)
      .catch(() => setCollaborators([]))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (isOpen && collaborators.length === 0) fetchCollaborators(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => fetchCollaborators(search), 300);
    return () => clearTimeout(id);
  }, [search, isOpen, fetchCollaborators]);

  const current = useMemo(
    () => collaborators.find((c) => c.email === recruiterEmail)
      ?? (recruiterEmail ? { id: 0, name: recruiterEmail, email: recruiterEmail } : null),
    [collaborators, recruiterEmail],
  );

  const assign = (email: string | null, name: string | null) => {
    onAssign(email, name);
    onClose();
  };

  const assignToMe = () => {
    if (meEmail) assign(meEmail, me?.name ?? meEmail);
  };

  const titleColor = variant === "header" ? nameClr : section.titleColor;
  const subColor   = variant === "header" ? muted : section.subColor;

  // sGatilho do dropdown: o próprio bloco com avatar/nome, com cara de campo
  // clicável (borda + hover), em vez de um botão "Atribuir"/"Trocar" à parte.
  const TriggerRow = (
    <Flex
      as="button"
      type="button"
      align="center"
      gap={3}
      flex={1}
      minW={0}
      px={3}
      py={2}
      borderRadius="md"
      borderWidth="1px"
      borderColor={rowBorder}
      textAlign="left"
      disabled={isSaving}
      opacity={isSaving ? 0.6 : 1}
      cursor={isSaving ? "not-allowed" : "pointer"}
      transition="border-color 0.15s, background 0.15s"
      _hover={isSaving ? undefined : { borderColor: rowHoverBorder, bg: rowHoverBg }}
      onClick={onOpen}
    >
      {current ? (
        <UserAvatar size="sm" name={current.name} email={current.email} flexShrink={0} />
      ) : (
        <Avatar size="sm" icon={<Icon as={User} boxSize={3} color={strongClr} />} bg={emptyAvatarBg} flexShrink={0} />
      )}
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="semibold" color={titleColor} noOfLines={1}>
          {current ? current.name : "Não atribuído"}
          {current && meEmail && current.email === meEmail && (
            <Text as="span" fontSize="xs" color={subColor} ml={1}>(você)</Text>
          )}
        </Text>
        {current && (
          <Text fontSize="xs" color={subColor} noOfLines={1}>{current.email}</Text>
        )}
      </Box>
      <Icon as={ChevronDown} boxSize={4} color={muted} flexShrink={0} />
    </Flex>
  );

  const Dropdown = (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" isLazy matchWidth>
      <PopoverTrigger>{TriggerRow}</PopoverTrigger>
      <PopoverContent w="320px" borderRadius="md" boxShadow="lg" bg={dropdownBg} borderColor={rowBorder}>
        <PopoverBody p={3}>
          <VStack align="stretch" spacing={2}>
            {meEmail && (
              <Button
                size="sm" colorScheme="blue" variant="solid"
                leftIcon={<UserPlus size={13} />}
                isDisabled={isSaving || recruiterEmail === meEmail}
                onClick={assignToMe}
              >
                Atribuir a mim
              </Button>
            )}
            <Input
              placeholder="Buscar colaborador..." size="sm" bg={inputBg} borderRadius="md"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            <Box maxH="260px" overflowY="auto">
              {current && (
                <Flex
                  align="center" gap={2} py={2} px={2} borderRadius="md"
                  cursor="pointer" _hover={{ bg: itemHover }}
                  onClick={() => assign(null, null)}
                >
                  <Avatar size="sm" icon={<Icon as={X} boxSize={3} />} bg={emptyAvatarBg} flexShrink={0} />
                  <Text fontSize="sm" color={muted}>Remover atribuição</Text>
                </Flex>
              )}
              {loading && collaborators.length === 0 ? (
                <Text fontSize="xs" color={muted} textAlign="center" py={3}>Carregando...</Text>
              ) : collaborators.length === 0 ? (
                <Text fontSize="xs" color={muted} textAlign="center" py={3}>Nenhum colaborador.</Text>
              ) : (
                collaborators.map((c) => (
                  <Flex
                    key={c.id} align="center" gap={2} py={2} px={2} borderRadius="md"
                    cursor="pointer" _hover={{ bg: itemHover }}
                    onClick={() => assign(c.email, c.name)}
                  >
                    <UserAvatar size="sm" name={c.name} email={c.email} flexShrink={0} />
                    <Box minW={0}>
                      <Text fontSize="sm" noOfLines={1}>{c.name}</Text>
                      <Text fontSize="xs" color={muted} noOfLines={1}>{c.email}</Text>
                    </Box>
                  </Flex>
                ))
              )}
            </Box>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );

  const AssignToMeLink = !current && meEmail ? (
    <Text
      as="button" type="button"
      p={0} m={0} minW={0} bg="transparent" border="none"
      fontSize="xs" color={linkClr} fontWeight="medium"
      alignSelf="flex-start" mt={1}
      disabled={isSaving}
      opacity={isSaving ? 0.6 : 1}
      cursor={isSaving ? "not-allowed" : "pointer"}
      _hover={isSaving ? undefined : { textDecoration: "underline" }}
      onClick={assignToMe}
    >
      Atribuir a mim
    </Text>
  ) : null;

  if (variant === "header") {
    return (
      <VStack align="stretch" spacing={1} mb={5}>
        <HStack spacing={1.5} color={strongClr}>
          <Icon as={User} boxSize={3.5} />
          <Text fontSize="xs" fontWeight="medium">Recrutador responsável</Text>
        </HStack>
        {Dropdown}
        {AssignToMeLink}
      </VStack>
    );
  }

  return (
    <Box p={4} bg={section.bg} borderRadius="md" borderWidth="1px" borderColor={section.borderColor}>
      <VStack align="stretch" spacing={1}>
        {Dropdown}
        {AssignToMeLink}
      </VStack>
    </Box>
  );
};
